import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type ProductsCollectionRequest,
  ProductPublicationStatus,
  ProductStockStatus,
  PRODUCT_QUERIES,
} from '@skin-seoul/service';
import { isEqual } from 'es-toolkit/compat';
import { type FilterSummary } from '../types/filters';
import { useProductGridCommon, useProductDataProcessing } from './shared/useProductGridCommon';

interface UseCollectionProductGridDataProps {
  collectionName: string;
  currentPage?: number;
  productsPerPage: number;
  onProductCountChange?: (count: number) => void;
  onFilterSummaryChange?: (summary: FilterSummary | undefined) => void;
}

export const useCollectionProductGridData = ({
  collectionName,
  currentPage = 1,
  productsPerPage,
  onProductCountChange,
  onFilterSummaryChange,
}: UseCollectionProductGridDataProps) => {
  const { currency, productService, pagination } = useProductGridCommon({
    currentPage,
    productsPerPage,
  });

  const previousResultRef = useRef<{
    normalizedParams: object;
    apiParams: ProductsCollectionRequest;
  } | null>(null);

  const apiParams = useMemo(() => {
    const params: ProductsCollectionRequest = {
      collectionName,
      page: pagination.page,
      limit: pagination.limit,
      publicationStatus: ProductPublicationStatus.publish,
      stockStatus: ProductStockStatus.instock,
      currency,
    };

    const normalizedParams = {
      collectionName: params.collectionName,
      currency: params.currency,
      limit: params.limit,
      page: params.page,
      publicationStatus: params.publicationStatus,
      stockStatus: params.stockStatus,
    };

    if (previousResultRef.current && isEqual(normalizedParams, previousResultRef.current.normalizedParams)) {
      return previousResultRef.current.apiParams;
    }

    previousResultRef.current = {
      normalizedParams,
      apiParams: params,
    };

    return params;
  }, [collectionName, currency, pagination]);

  // For skinseoulselects, fetch total count separately to work around API bug
  // TODO: Remove this once the API is fixed to return correct total with normal pagination
  const shouldFetchTotalCount = collectionName === 'skinseoulselects';

  const totalCountParams = useMemo(
    () => ({
      collectionName,
      page: 1,
      limit: 1000,
      publicationStatus: ProductPublicationStatus.publish,
      stockStatus: ProductStockStatus.instock,
      currency,
    }),
    [collectionName, currency],
  );

  const { data: totalCountData } = useQuery({
    ...PRODUCT_QUERIES.getProductsCollections(totalCountParams),
    enabled: shouldFetchTotalCount,
    staleTime: Infinity, // Cache indefinitely since total won't change often
  });

  const { data } = productService.useGetProductsCollections(apiParams);

  // For skinseoulselects, override the total with the correct count from our separate query
  // TODO: Remove this once the API is fixed to return correct total with normal pagination
  const processedData = useMemo(() => {
    if (!data) {
      return data;
    }

    if (collectionName === 'skinseoulselects' && totalCountData) {
      return {
        ...data,
        total: totalCountData.total, // Use the correct total from the full fetch
        summary: undefined,
      };
    }

    return { ...data, summary: undefined };
  }, [data, collectionName, totalCountData]);

  return useProductDataProcessing({
    data: processedData,
    currentPage,
    onProductCountChange,
    onFilterSummaryChange,
  });
};
